<!-- Külje pealt avanev ostukorv -->
<template>
  <div class="clickable" @click.stop="drawer = !drawer">
    <v-badge v-if="cartItemCount > 0" color="red" :content="cartItemCount">
      <v-icon class="bi bi-bag"></v-icon>
    </v-badge>
  </div>
  <v-layout class="v-overlay-scroll-blocked">
    <v-navigation-drawer v-model="drawer" width="500" location="right" temporary touchless>
      <v-toolbar color="white">
        <v-btn aria-label="close" icon="mdi-close" @click="drawer = false"></v-btn>
        <v-toolbar-title>{{ $t('ostukorv') }}</v-toolbar-title>
      </v-toolbar>
      <v-list class="pa-6" aria-label="Product List" role="listbox">
        <v-list-item class="pa-0" v-for="(item, productId) in cart" :key="productId" role="option" aria-selected="false">
          <div class="d-flex w-100 my-6 align-center cart-content">
            <a class="d-flex align-center flex-1-1 mr-6" :href="'/tooted/' + getLinkCat(item.product) + '/' + item.product.id">
              <div class="d-flex align-center justify-center mr-5 cart-img">
                <v-img v-if="getCartImage(item.product)" width="80" height="80" cover :src="getCartImage(item.product)" draggable="false"></v-img>
                <img class="cart-icon" v-else src="../assets/images/lohnakas.webp" alt="Lohnakas logo" draggable="false"/>
              </div>
              <div class="d-flex flex-column w-100">
                <span class="font-weight-bold text-left"> {{ getHeader(item.product) }} </span>
                <span v-if="hasSprayRollOnOption(item.product) || hasBottleSizeOption(item.product)" class="text-caption text-grey-darken-1">
                  <span v-if="hasSprayRollOnOption(item.product)">{{ item.choice || 'Spray' }}</span>
                  <span v-if="hasSprayRollOnOption(item.product) && hasBottleSizeOption(item.product)"> · </span>
                  <span v-if="hasBottleSizeOption(item.product)">{{ item.size || '100ml' }}</span>
                </span>
              </div>
            </a>
            <div class="cart-item-controls d-flex flex-column">
              <div class="flex-column d-flex text-center">
                <div class="product-price font-weight-bold">
                  <div class="price-pill">
                    <span v-if="getOriginalLineTotal(item) !== null" class="old-price">
                      {{ formatPrice(getOriginalLineTotal(item)) }}
                    </span>
                    <span class="price-value">{{ formatPrice(getLineTotal(item)) }}</span>
                    <span class="price-currency">EUR</span>
                  </div>
                </div>
              </div>
              <div class="cart-quantity-control quantity-picker">
                <v-btn aria-label="Decrease Quantity" color="eraklient" @click="decreaseQuantity(productId)" icon="mdi-minus" size="30px"><v-icon size="20px"></v-icon></v-btn>
                <div class="quantity-input-wrap">
                  <input class="quantity-input" :title="$t('valige_kogus')" role="textbox" :aria-label="$t('quantity_input')" v-model="item.quantity" variant="outlined"/>
                </div>
                <v-btn aria-label="Increase Quantity" color="eraklient" @click="increaseQuantity(productId)" icon="mdi-plus" size="30px"><v-icon size="20px"></v-icon></v-btn>
              </div>
            </div>
          </div>
        </v-list-item>
        <div v-if="Object.keys(cart).length > 0">
          <v-divider class="mx-3"></v-divider>
          <div class="order-items my-6">
            <div class="text-uppercase">{{ $t('kokku') }}</div>
            <div class="font-weight-bold price-pill price-pill--summary">
              <span class="price-value">{{ formatPrice($store.getters.cartTotal) }}</span>
              <span class="price-currency">EUR</span>
            </div>
          </div>
          <div class="d-flex flex-column">
            <v-btn aria-label="toCart" class="cart-action-btn cart-action-btn--primary" color="eraklient" to="/tooted/ostukorv" @click="drawer = false">{{ $t('suundu_ostukorvi') }}</v-btn>
            <v-btn aria-label="deleteCart" class="my-2 cart-action-btn cart-action-btn--danger" color="error" aria-haspopup="true" :aria-expanded="empty_cart" @click="empty_cart = true">{{$t('tühjenda_korv')}}</v-btn>
          </div>
        </div>
        <v-dialog v-model="empty_cart" width="auto" transition="none">
          <v-card>
            <v-toolbar color="white" :title="$t('kinnitus')">
              <v-btn aria-label="confirmation" icon="mdi-close" @click="empty_cart = false"></v-btn>
            </v-toolbar>
            <v-card-text>
              <div class="text-h5 justify-center text-center">{{$t('eemalda_conf')}}</div>
            </v-card-text>
            <v-divider></v-divider>
            <div class="pa-4">
              <v-btn aria-label="deleteCart" class="text-none mr-4 font-weight-bold" color="eraklient" min-width="92" rounded @click.prevent="deleteCart" @click="empty_cart = false; showToastSuccess($t('tuhjendatud'))">{{$t('jah')}}</v-btn>
              <v-btn aria-label="cancel" class="text-none font-weight-bold" min-width="92" rounded @click="empty_cart = false">{{$t('ei')}}</v-btn>
            </div>
          </v-card>
        </v-dialog>
        <p role="option" v-if="Object.keys(cart).length === 0">{{ $t('ostukorv_tuhi') }}</p>
      </v-list>
    </v-navigation-drawer>
  </v-layout>
</template>

<script>
import {toast} from "vue3-toastify";
import { formatPriceValue, getProductUnitPrice, hasBottleSizePriceRange as productHasBottleSizePriceRange, isBottleSizeOptionEnabled, isSprayRollOnOptionEnabled } from "@/utils/productOptions.js";

export default {
  name: "ShoppingCart",
  data() {
    return {
      drawer: false,
      empty_cart: false
    };
  },
  computed: {
    cartItemCount() {
      return this.$store.getters.cartItemCount;
    },
    cart() {
      return this.$store.state.cart;
    }
  },
  methods: {
    increaseQuantity(productId) {
      this.$store.commit('increaseQuantity', productId);
    },
    decreaseQuantity(productId) {
      this.$store.commit('decreaseQuantity', productId);
    },
    getHeader(content) {
      return (this.$i18n.locale === 'en' && content.header_en !== null) ? content.header_en : (this.$i18n.locale === 'ru' && content.header_ru !== null) ? content.header_ru : content.header;
    },
    getCartImage(content) {
      const image = content?.image;

      if (!image) return null;

      if (Array.isArray(image)) {
        return image[0] || null;
      }

      if (typeof image === 'string') {
        const trimmed = image.trim();

        if (!trimmed) return null;

        if (trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(trimmed);
            return Array.isArray(parsed) ? parsed[0] || null : null;
          } catch (err) {
            console.log(err);
          }
        }

        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
          const values = trimmed
              .slice(1, -1)
              .split(',')
              .map((value) => value.replace(/^"|"$/g, '').trim())
              .filter(Boolean);
          return values[0] || null;
        }

        return trimmed;
      }

      return null;
    },
    getLinkCat(content) {
      return this.$store.getters.getLinkCat(content);
    },
    hasSprayRollOnOption(content) {
      return isSprayRollOnOptionEnabled(content);
    },
    hasBottleSizeOption(content) {
      return isBottleSizeOptionEnabled(content);
    },
    hasBottleSizePriceRange(content) {
      return productHasBottleSizePriceRange(content);
    },
    getLineTotal(item) {
      return getProductUnitPrice(item.product, item.size) * Number(item.quantity || 0);
    },
    getOriginalLineTotal(item) {
      if (this.hasBottleSizePriceRange(item.product) || !item.product?.new_price) {
        return null;
      }

      return Number(item.product.price) * Number(item.quantity || 0);
    },
    formatPrice(value) {
      return formatPriceValue(value);
    },
    deleteCart() {
      this.drawer = false;
      this.$store.commit('deleteCart');
    },
    showToastSuccess(value) {
      toast.success(value, {
        autoClose: 3000
      })
    }
  }
}
</script>

<style scoped>
.v-badge {
  display: flex;
}

.product-price {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.cart-item-controls {
  gap: 10px;
  align-items: flex-end;
  min-width: 132px;
}

.cart-quantity-control {
  width: 132px;
  justify-content: space-between;
}

.quantity-picker {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.quantity-input-wrap {
  width: 48px;
  min-width: 48px;
  max-width: 48px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.05);
  flex: 0 0 48px;
}

.quantity-input {
  width: 100%;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1;
  color: #111827;
  outline: none;
}

.price-pill {
  width: auto;
  min-width: 132px;
  justify-content: center;
}

.price-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  background: linear-gradient(135deg, #f6fbff 0%, #e3efff 100%);
  border: 1px solid rgba(59, 113, 247, 0.18);
  box-shadow: 0 10px 24px rgba(59, 113, 247, 0.12);
  color: #13315c;
}

.price-pill--summary {
  padding: 6px 12px;
  box-shadow: none;
}

.price-value {
  font-size: 0.95rem;
  font-weight: 800;
  line-height: 1;
}

.price-currency {
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(19, 49, 92, 0.08);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.old-price {
  color: rgba(17, 24, 39, 0.55);
  font-size: 0.8rem;
  text-decoration: line-through;
}

.price-pill:has(.old-price) .price-value {
  color: #e53935;
}

.cart-action-btn {
  border-radius: 999px !important;
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.16) !important;
}

.cart-action-btn--primary {
  background: linear-gradient(135deg, #3b71f7 0%, #5b8cff 100%) !important;
  color: #ffffff !important;
}

.cart-action-btn--danger {
  background: linear-gradient(135deg, #b10028 0%, #cf0f3b 100%) !important;
  color: #ffffff !important;
}

.cart-action-btn :deep(.v-btn__content) {
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-transform: uppercase;
}

.cart-action-btn :deep(.v-btn__overlay) {
  opacity: 0 !important;
}

@media (max-width: 400px) {
  .cart-img {
    display: none !important;
  }

  .cart-quantity-control {
    gap: 8px;
  }
}
</style>